/**
 * Legend Container Component
 * Unified container for all map legends with accordion sections
 */

import React, { useMemo } from 'react';
import { Card } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { LegendContainerProvider } from './LegendContainerContext';
import { LegendSection } from './LegendSection';
import {
  ReportLegendContent,
  GPSLostLegendContent,
  WeatherLegendContent,
  WindBarbLegendContent,
  CoverageLegendContent,
  ColorByLegendContent,
} from './sections';
import { useReportContext } from '../../Reports/ReportContext';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import {
  useWeatherLegend,
  useResolvedWeatherLegend,
  ActiveLegendEntry,
} from '../WeatherLegend';
import { useCoverage } from '../Coverage/CoverageContext';
import { useColorByPin } from '../ColorTargetsBy/ColorByPinContext';

interface LegendContainerProps {
  className?: string;
  style?: React.CSSProperties;
}

interface ActiveLegends {
  hasReport: boolean;
  hasGPSLost: boolean;
  hasWeather: boolean;
  hasCoverage: boolean;
  hasColorBy: boolean;
}

/** ABB layers that use wind barb symbology (not direction arrows or wave arrows). */
const WIND_BARB_LAYER_PATTERN = /wind-symbol/i;

function isWindBarbLayer(layerId: string): boolean {
  return WIND_BARB_LAYER_PATTERN.test(layerId);
}

/** Renders a single weather legend section with its own data fetching. */
const WeatherLegendSection: React.FC<{
  entry: ActiveLegendEntry;
  timepointLabel?: string;
}> = ({ entry, timepointLabel }) => {
  const { legend, subtitle, unit, isLoading } = useResolvedWeatherLegend(
    entry,
    timepointLabel,
  );

  // If the layer has a color-table legend, show it
  if (!isLoading && legend && legend.length > 0) {
    return (
      <LegendSection
        type="weather"
        title={entry.label}
        subtitle={subtitle || undefined}
        subtitleWrap
      >
        <WeatherLegendContent legend={legend} />
      </LegendSection>
    );
  }

  // For wind barb / directional layers without color legends, show the barb legend
  if (!isLoading && isWindBarbLayer(entry.layerId)) {
    return (
      <LegendSection
        type="weather"
        title={entry.label}
        subtitle={subtitle || undefined}
        subtitleWrap
      >
        <WindBarbLegendContent />
      </LegendSection>
    );
  }

  // For value-grid layers (numbers on tiles), show the unit from metadata
  if (!isLoading && unit) {
    return (
      <LegendSection
        type="weather"
        title={entry.label}
        subtitle={subtitle || undefined}
        subtitleWrap
      >
        <p className="text-xs text-muted-foreground">Values in {unit}</p>
      </LegendSection>
    );
  }

  return null;
};

const LegendContainerInner: React.FC<LegendContainerProps> = ({
  className,
  style,
}) => {
  const { isPinned: colorByPinned } = useColorByPin();

  // Get legend data from various contexts
  const { legend: reportLegend } = useReportContext();
  const { gpsLostLegend } = useGPSLost();
  const { activeLegends: weatherLegends, timepointLabels } = useWeatherLegend();
  const { showCoverageLegend, coverageLegend } = useCoverage();

  // Determine which legends are active
  const activeLegends: ActiveLegends = useMemo(
    () => ({
      hasReport: !!(reportLegend && reportLegend.items.length > 0),
      hasGPSLost: !!gpsLostLegend,
      hasWeather: weatherLegends.length > 0,
      hasCoverage: showCoverageLegend,
      hasColorBy: colorByPinned,
    }),
    [
      reportLegend,
      gpsLostLegend,
      weatherLegends,
      showCoverageLegend,
      colorByPinned,
    ],
  );

  // Count active legends
  const activeLegendCount = useMemo(() => {
    return Object.values(activeLegends).filter(Boolean).length;
  }, [activeLegends]);

  // If no legends are active, don't render anything
  if (activeLegendCount === 0) {
    return null;
  }

  return (
    <Card
      style={style}
      className={cn('h-fit w-fit min-w-[360px] max-w-[400px]', className)}
    >
      {/* Legend Sections */}
      <div className="divide-y divide-border">
        {activeLegends.hasColorBy && (
          <LegendSection type="colorBy" title="Vessel Colors">
            <ColorByLegendContent />
          </LegendSection>
        )}

        {activeLegends.hasReport && reportLegend && (
          <LegendSection
            type="report"
            title={reportLegend.title}
            subtitle={reportLegend.subtitle}
          >
            <ReportLegendContent legend={reportLegend} />
          </LegendSection>
        )}

        {activeLegends.hasGPSLost && gpsLostLegend && (
          <LegendSection
            type="gpsLost"
            title={gpsLostLegend.title}
            subtitle={gpsLostLegend.subtitle}
          >
            <GPSLostLegendContent legend={gpsLostLegend} />
          </LegendSection>
        )}

        {weatherLegends.map((entry) => (
          <WeatherLegendSection
            key={entry.layerId}
            entry={entry}
            timepointLabel={timepointLabels[entry.layerId]}
          />
        ))}

        {activeLegends.hasCoverage && (
          <LegendSection
            type="coverage"
            title={coverageLegend.title}
            subtitle={coverageLegend.subtitle}
          >
            <CoverageLegendContent />
          </LegendSection>
        )}
      </div>
    </Card>
  );
};

export const LegendContainer: React.FC<LegendContainerProps> = (props) => {
  return (
    <LegendContainerProvider>
      <LegendContainerInner {...props} />
    </LegendContainerProvider>
  );
};

export default LegendContainer;
